import { useMsal } from "@azure/msal-react"
import { Alert, Button } from "@relex-solutions/fragments"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"
import { loginRequest } from "@/shared/config/msalConfig"
import { AUTH_METHOD } from "@/shared/constants/authMethods"
import { HOME_PATH } from "@/shared/constants/routePaths"
import { useAuth } from "@/shared/hooks/useAuth"
import { login as oidcLogin } from "@/utils/login"
import styles from "./LoginPage.module.css"

export function LoginPage() {
  const auth = useAuth()
  const { instance } = useMsal()
  const { t } = useTranslation()
  const [showOidcFallback, setShowOidcFallback] = useState(false)
  const location = useLocation()

  const isAuthenticated = auth.user !== null
  const navigate = useNavigate()

  // Extract redirect from query string
  const searchParams = new URLSearchParams(location.search)
  const redirect = searchParams.get("redirect")

  const handleMsalLogin = async () => {
    // Always attempt MSAL login using the instance directly
    // This works regardless of the current auth method in state
    await instance.loginPopup(loginRequest)
    // Switch to MSAL auth method after successful login
    window.localStorage.setItem("authMethod", AUTH_METHOD.MSAL)
    // Redirect to previous URL if present
    if (redirect) {
      window.location.href = decodeURIComponent(redirect)
    } else {
      navigate(HOME_PATH)
    }
  }

  const handleOidcFallback = () => {
    oidcLogin()
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <header className={styles.header}>
          <h1>{t("login.pageTitle")}</h1>
          <p className={styles.subtitle}>{t("login.subtitle")}</p>
        </header>

        <section className={styles.loginSection}>
          <MsalLogin
            isAuthenticated={isAuthenticated}
            login={handleMsalLogin}
            onError={() => {
              setShowOidcFallback(true)
            }}
          />

          {showOidcFallback && (
            <div className={styles.fallbackSection}>
              <Alert color="warning" header={t("login.msalFailureTitle")}>
                {t("login.msalFailureMessage")}
              </Alert>
              <Button onClick={handleOidcFallback} variant="secondary">
                {t("login.signInOidc")}
              </Button>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

function MsalLogin({
  isAuthenticated,
  login,
  onError
}: {
  isAuthenticated: boolean
  login: () => Promise<void>
  onError: (error: Error) => void
}) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  if (isAuthenticated) {
    return <AuthenticatedState />
  }

  return (
    <Button
      onClick={async () => {
        try {
          setIsLoading(true)
          await login()
        } catch (error) {
          const { captureException } = await import("@sentry/react")
          const { logger } = await import("@/shared/utils/add/logger/logger")
          const err = error instanceof Error ? error : new Error("Entra ID login error", { cause: error })
          logger.error(err.message, err)
          captureException(err)
          onError(err)
        } finally {
          setIsLoading(false)
        }
      }}
      isLoading={isLoading}
    >
      {t("login.signInMsal")}
    </Button>
  )
}

function AuthenticatedState() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  return (
    <div>
      <p>{t("login.alreadySignedIn")}</p>
      <Button
        onClick={() => {
          navigate(HOME_PATH)
        }}
      >
        {t("login.goToHome")}
      </Button>
    </div>
  )
}
